import React from "react";
import { Box, Typography, Button } from "@mui/material";
import CommonModal from "../CommonModal";

/**
 * AttemptsInfoModal - Optimized modal for displaying run attempts information
 * and encouraging use of custom input feature
 */
const AttemptsInfoModal = ({
  open,
  onClose,
  attemptsUsed,
  maxAttempts,
  attemptsExhausted,
  isAutoOpened = false,
  // ✅ REMOVED: onModalStateChange - no longer needed without per-question timers
  onGoToCustomInput,
}) => {
  // ✅ REMOVED: Timer pause logic - programming questions no longer have per-question timers

  const handleClose = () => {
    onClose();
  };

  const handleGoToCustomInput = () => {
    onGoToCustomInput();
  };

  return (
    <CommonModal
      icon="/assets/circlecheck_green.svg"
      handleClose={handleClose}
      open={open}
      title="About Run Attempts & Custom Input"
      showFooter={false}
      sx={{
        zIndex: 10003, // Higher than fullscreen editor (9999) and header (10001)
      }}
      PaperProps={{
        sx: {
          zIndex: 10003,
        },
      }}
      BackdropProps={{
        sx: {
          zIndex: 10002,
        },
      }}
    >
      <Box
        sx={{
          p: { base: 2, md: 3 },
          display: "flex",
          flexDirection: "column",
          gap: { base: 2, md: 3 },
          maxHeight: { base: "60vh", md: "70vh" },
          overflowY: "auto",
          overflowX: "hidden",
          // Custom scrollbar styling
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f1f1f1",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#8b8d8cff",
            borderRadius: "4px",
            "&:hover": {
              backgroundColor: "#00A085",
            },
          },
        }}
      >
        {/* Current Attempts Status - Compact */}
        <Box
          sx={{
            bgcolor: attemptsExhausted ? "#FFEBEE" : "#FFF3E0",
            p: 2,
            borderRadius: "8px",
            border: attemptsExhausted
              ? "1px solid #FFCDD2"
              : "1px solid #FFE0B2",
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: { base: "14px", md: "16px" },
              fontWeight: 600,
              color: attemptsExhausted ? "#C62828" : "#F57C00",
              mb: 0.5,
            }}
          >
            {attemptsExhausted
              ? "❌ All Attempts Used"
              : `✅ ${maxAttempts - attemptsUsed} Attempt${
                  maxAttempts - attemptsUsed === 1 ? "" : "s"
                } Remaining`}
          </Typography>
          <Typography
            sx={{
              fontSize: { base: "12px", md: "13px" },
              color: attemptsExhausted ? "#C62828" : "#F57C00",
            }}
          >
            Used {attemptsUsed} of {maxAttempts} attempts
          </Typography>
        </Box>

        {/* Key Message - Custom Input Benefit */}
        <Box
          sx={{
            bgcolor: "#E8F5E9",
            p: 2,
            borderRadius: "8px",
            border: "1px solid #C8E6C9",
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: { base: "14px", md: "15px" },
              fontWeight: 600,
              mb: 1,
              color: "#2E7D32",
            }}
          >
            🎯 Test Unlimited Times with Custom Input
          </Typography>
          <Typography
            sx={{
              fontSize: { base: "13px", md: "14px" },
              color: "#1B5E20",
              lineHeight: 1.6,
              mb: 1.5,
            }}
          >
            Use the <strong>"Custom Input"</strong> tab to test your code
            unlimited times without consuming run attempts. Perfect for
            debugging before final verification.
          </Typography>
        </Box>

        {/* Action Button */}
        <Button
          variant="contained"
          onClick={handleGoToCustomInput}
          sx={{
            bgcolor: "#00C49A",
            color: "#fff",
            fontWeight: 600,
            textTransform: "none",
            py: { base: 1.5, md: 2 },
            fontSize: { base: "14px", md: "16px" },
            flexShrink: 0,
            "&:hover": {
              bgcolor: "#00A085",
            },
          }}
        >
          Go to Custom Input
        </Button>
      </Box>
    </CommonModal>
  );
};

export default AttemptsInfoModal;
